import { useState, useCallback } from 'react';

const FAVORITES_KEY = 'ltbb_favorite_loops';

// Initialize state from localStorage
const getInitialFavorites = () => {
  try {
    const stored = localStorage.getItem(FAVORITES_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    console.error('Error loading favorites:', e);
  }
  return [];
};

export const useFavorites = () => {
  const [favorites, setFavorites] = useState(getInitialFavorites);
  
  // Check if a loop is favorited
  const isFavorite = useCallback((loopId) => {
    return favorites.some(l => l.loop_id === loopId);
  }, [favorites]);
  
  // Toggle favorite status
  const toggleFavorite = useCallback((loop) => {
    setFavorites(prev => {
      const exists = prev.some(l => l.loop_id === loop.loop_id);
      let updated;
      
      if (exists) {
        // Remove from favorites
        updated = prev.filter(l => l.loop_id !== loop.loop_id);
      } else {
        // Add to favorites (at the beginning)
        updated = [loop, ...prev];
      }
      
      // Save to localStorage
      try {
        localStorage.setItem(FAVORITES_KEY, JSON.stringify(updated));
      } catch (e) {
        console.error('Error saving favorites:', e);
      }
      
      return updated;
    });
    
    // Return the new state (favorited or not)
    return !favorites.some(l => l.loop_id === loop.loop_id);
  }, [favorites]);
  
  // Add a loop to favorites
  const addFavorite = useCallback((loop) => {
    setFavorites(prev => {
      if (prev.some(l => l.loop_id === loop.loop_id)) {
        return prev; // Already favorited
      }
      const updated = [loop, ...prev];
      try {
        localStorage.setItem(FAVORITES_KEY, JSON.stringify(updated));
      } catch (e) {
        console.error('Error saving favorites:', e);
      }
      return updated;
    });
  }, []);
  
  // Remove a loop from favorites
  const removeFavorite = useCallback((loopId) => {
    setFavorites(prev => {
      const updated = prev.filter(l => l.loop_id !== loopId);
      try {
        localStorage.setItem(FAVORITES_KEY, JSON.stringify(updated));
      } catch (e) {
        console.error('Error saving favorites:', e);
      }
      return updated;
    });
  }, []);
  
  // Clear all favorites
  const clearFavorites = useCallback(() => {
    setFavorites([]);
    localStorage.removeItem(FAVORITES_KEY);
  }, []);
  
  return { 
    favorites, 
    isFavorite, 
    toggleFavorite, 
    addFavorite, 
    removeFavorite, 
    clearFavorites,
    favoritesCount: favorites.length
  };
};
